import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Receipt } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

const CONTENT = {
    deposit: {
        tag: "Deposit Confirmed",
        title: "Deposit received.",
        gradient: "We're building.",
        sub: "Your project is officially locked in and on the build schedule. Here's exactly what happens from here.",
        steps: [
            { title: "Kickoff within 24 hours", text: "I'll personally reach out by email or text to confirm the details and get things moving." },
            { title: "We shape the vision", text: "A few quick questions about style, pages, and content — then I get to work." },
            { title: "Design, build, review", text: "You review previews and request tweaks until it's exactly right. The remaining balance is due only when your site is complete." },
        ],
    },
    hosting: {
        tag: "Hosting Active",
        title: "You're covered.",
        gradient: "Site stays live.",
        sub: "Hosting & maintenance is now active for your website. There's nothing else you need to do.",
        steps: [
            { title: "Everything stays on", text: "Your website stays live, secure, and operational — hosting, minor updates, and support are all handled." },
            { title: "Simple monthly billing", text: "$25/month, billed automatically to your card. Stripe emails you a receipt every month." },
            { title: "Cancel anytime", text: "No lock-in. You always own your domain and your content — text or email me anytime for changes or questions." },
        ],
    },
    design: {
        tag: "Order Confirmed",
        title: "Order received.",
        gradient: "Design starts now.",
        sub: "Thanks for your order — I'm getting started on your design right away.",
        steps: [
            { title: "I start designing", text: "First concepts land in your inbox — keep an eye out for an email from me." },
            { title: "You review", text: "Request tweaks until it feels exactly right. Revisions are part of the process." },
            { title: "Final delivery", text: "Polished digital files ready for your website, socials, and print." },
        ],
    },
    default: {
        tag: "Payment Confirmed",
        title: "Payment received.",
        gradient: "Thank you.",
        sub: "Your payment went through securely. Here's what happens next.",
        steps: [
            { title: "Confirmation", text: "Stripe emails your receipt automatically — keep it for your records." },
            { title: "I reach out", text: "You'll hear from me personally within 24 hours with next steps." },
            { title: "Questions anytime", text: "Text or email me anytime — you always talk directly to the person building your project." },
        ],
    },
};

const TYPE_MAP = { deposit: "deposit", hosting: "hosting", logo: "design", branding: "design", update: "default" };

export default function PaymentSuccess() {
    const { CONTACT_EMAIL } = useSiteContent();
    const [params] = useSearchParams();
    const c = CONTENT[TYPE_MAP[params.get("type")] || "default"];

    return (
        <div data-testid="payment-success-page">
            <SEO
                title="Payment Received | Camarca Digital Designs"
                description="Your payment was received securely. Here's what happens next."
                path="/payment-success"
                noindex
            />

            <section className="mx-auto flex min-h-screen max-w-4xl flex-col items-center px-6 pb-24 pt-40 text-center lg:px-10">
                {/* Animated check */}
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 16, delay: 0.15 }}
                    className="relative flex h-24 w-24 items-center justify-center rounded-full border border-neon/40 bg-surface shadow-[0_0_45px_rgba(0,240,255,0.25)]"
                    data-testid="payment-success-icon"
                >
                    <span className="absolute inset-0 animate-ping rounded-full border border-neon/20" aria-hidden="true" />
                    <svg viewBox="0 0 24 24" className="h-10 w-10" aria-hidden="true">
                        <motion.path
                            d="M5 13l4 4L19 7"
                            fill="none"
                            stroke="#00F0FF"
                            strokeWidth={2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
                        />
                    </svg>
                </motion.div>

                <Reveal delay={0.35}>
                    <div className="mt-10"><SectionTag>{c.tag}</SectionTag></div>
                    <h1
                        data-testid="payment-success-title"
                        className="mt-6 font-display text-4xl font-black leading-tight tracking-tight md:text-6xl"
                    >
                        {c.title}{" "}
                        <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">{c.gradient}</span>
                    </h1>
                    <p data-testid="payment-success-sub" className="mx-auto mt-6 max-w-xl leading-relaxed text-zinc-400 md:text-lg">
                        {c.sub}
                    </p>
                </Reveal>

                {/* What happens next */}
                <div className="mt-16 grid w-full gap-6 text-left md:grid-cols-3">
                    {c.steps.map((s, i) => (
                        <Reveal key={s.title} delay={0.45 + i * 0.12}>
                            <div
                                data-testid={`payment-step-${i}`}
                                className="h-full border border-white/10 bg-surface p-6 transition-[border-color] duration-500 hover:border-white/30"
                            >
                                <p className="font-mono text-xs tracking-[0.3em] text-neon">0{i + 1}</p>
                                <h2 className="mt-3 font-display text-base font-bold">{s.title}</h2>
                                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.85}>
                    <p
                        data-testid="payment-receipt-note"
                        className="mt-12 flex items-center justify-center gap-3 border border-white/10 bg-surface px-6 py-4 font-mono text-xs leading-relaxed tracking-wider text-zinc-400"
                    >
                        <Receipt className="h-4 w-4 shrink-0 text-neon" />
                        A receipt from Stripe is on its way to your email inbox.
                    </p>
                </Reveal>

                <Reveal delay={0.95}>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link
                            to="/work"
                            data-testid="payment-success-work-link"
                            className="group inline-flex items-center gap-3 bg-neon px-7 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                        >
                            See my work
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="payment-success-email-link"
                            className="group inline-flex items-center gap-3 border border-neon/70 px-7 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color] duration-300 hover:bg-neon hover:text-void"
                        >
                            <Mail className="h-4 w-4" />
                            Email me
                        </a>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
